import { type FormEvent, type ReactElement, useState } from 'react';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { AuthLayout } from '../components/layout';
import { Button, Field, Notice, TextInput, AuthCard, Stack, Text } from '@vello/web-theme';

/** Read the single-use recovery token from the reset link (`/recovery/reset?token=…`). */
function tokenFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('token') ?? '';
  } catch {
    return '';
  }
}

/**
 * Operator password-recovery completion (Phase 2; D304/D305). The landing for the reset link emailed by the
 * recovery flow: the operator sets a new password, which is applied via `POST /v1/operator/recovery/complete`
 * (single-use token, server-enforced policy). On success it returns to login. Shares the light auth card.
 */
export function RecoveryResetScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();

  const [token] = useState<string>(() => tokenFromUrl());
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    if (token === '') {
      setMsg({ tone: 'danger', text: t('recovery.reset.noToken') });
      return;
    }
    if (password !== confirm) {
      setMsg({ tone: 'danger', text: t('recovery.reset.mismatch') });
      return;
    }
    setBusy(true);
    const res = await runtime.operatorAuth.completeRecovery({ token, newPassword: password });
    setBusy(false);
    if (res.ok) {
      setDone(true);
      setMsg({ tone: 'success', text: t('recovery.reset.done') });
      return;
    }
    // The server returns a uniform 400 for an invalid/expired token AND a weak password; the safeMessage
    // distinguishes them for the operator, but we key the copy on the code and show the generic guidance.
    const detail = res.error.detail ?? '';
    const text = detail.includes('policy') ? t('recovery.reset.weak') : detail.includes('expired') || detail.includes('invalid') ? t('recovery.reset.invalid') : t('recovery.reset.failed', { code: res.error.code });
    setMsg({ tone: 'danger', text });
  };

  return (
    <AuthLayout>
      <AuthCard>
        <h1 className="vello-auth-heading__title">{t('recovery.reset.title')}</h1>
        <Text as="p" tone="secondary">{t('recovery.reset.intro')}</Text>
        {done ? (
          <>
            {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
            <Button variant="primary" block onClick={() => navigate('login')}>
              {t('recovery.reset.back')}
            </Button>
          </>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} noValidate>
            <Stack gap={4}>
              <Field label={t('recovery.reset.newPassword')} htmlFor="reset-password" hint={t('recovery.reset.newPassword.hint')}>
                <TextInput id="reset-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </Field>
              <Field label={t('recovery.reset.confirm')} htmlFor="reset-confirm">
                <TextInput id="reset-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </Field>
              {msg !== undefined && (
                <Notice tone={msg.tone}>{msg.text}</Notice>
              )}
              <Stack gap={2}>
                <Button type="submit" variant="primary" block disabled={busy}>
                  {busy ? t('recovery.reset.busy') : t('recovery.reset.submit')}
                </Button>
                <Button block onClick={() => navigate('login')}>{t('recovery.reset.back')}</Button>
              </Stack>
            </Stack>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
